/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { newsletterOptions } from '../options/newsletter.options';

/**
 * Newsletter directive
 *
 * Enhances the server-rendered subscription form:
 *  1. Client-side email format validation.
 *  2. reCAPTCHA v2 execution via `window.recaptchaPlugin` (invisible widget).
 *  3. AJAX JSON submission to `Page-SubmitNewsletterLead`.
 *  4. Swap the form for the success UI (or show an inline error).
 *
 * Progressive enhancement: if the directive never initialises the form still
 * performs a native POST to its `action`.
 */
export const newsletterDirective = {
  /**
   * @protected
   */
  options: newsletterOptions,

  /**
   * Per-instance state, keyed by `element.ambid`.
   *
   * @type {Map<string, object>}
   */
  container: new Map(),

  /**
   * @param {HTMLElement} element Wrapper decorated with `amb-newsletter`.
   * @param {object}      options Merged options from the compiler.
   */
  init(element, options = {}) {
    const form = element.tagName === 'FORM' ? element : element.querySelector('form');

    if (!form) {
      return;
    }

    const emailInput = form.querySelector('input[type="email"]');
    const submitBtn = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-newsletter-error]');
    const successEl = element.querySelector('[data-newsletter-success]') || form.querySelector('[data-newsletter-success]');
    const introEl = element.querySelector('[data-newsletter-intro]');

    if (!emailInput || !submitBtn) {
      return;
    }

    let emailRegex;

    try {
      emailRegex = new RegExp(options.emailPattern || '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$');
    } catch (error) {
      emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    }

    const submitListener = (event) => {
      event.preventDefault();
      this.handleSubmit({ form, emailInput, submitBtn, errorEl, successEl, introEl, emailRegex, options });
    };

    // Clear any error state as soon as the user edits the input.
    const inputListener = () => {
      this.clearState(emailInput, errorEl);
    };

    form.addEventListener('submit', submitListener);
    emailInput.addEventListener('input', inputListener);

    this.container.set(element.ambid, { form, emailInput, submitListener, inputListener });
  },

  /**
   * @param {HTMLElement} element
   */
  destroy(element) {
    const state = this.container.get(element.ambid);

    if (state) {
      state.form.removeEventListener('submit', state.submitListener);

      if (state.emailInput && state.inputListener) {
        state.emailInput.removeEventListener('input', state.inputListener);
      }

      this.container.delete(element.ambid);
    }
  },

  /**
   * Validate, run reCAPTCHA and submit.
   *
   * @param {object} ctx
   */
  handleSubmit(ctx) {
    const { form, emailInput, submitBtn, errorEl, successEl, introEl, emailRegex, options } = ctx;

    // Preview states are Edit Mode only — never submit from a forced preview.
    if (options.previewState && options.previewState !== 'auto') {
      return;
    }

    this.clearState(emailInput, errorEl);

    const email = (emailInput.value || '').trim();

    if (!emailRegex.test(email)) {
      this.showError(emailInput, errorEl, options.emailInvalidMessage);
      emailInput.focus();
      return;
    }

    this.setLoading(form, submitBtn, true);

    this.executeCaptcha(form, options.recaptchaAction)
      .then((token) => this.postLead(form.action, email, token, this.getOrigin(), options))
      .then(() => {
        form.reset();
        this.showSuccess(form, emailInput, errorEl, successEl, introEl);
        document.dispatchEvent(new CustomEvent('amb:newsletter-success', {
          detail: { form: form, element: form.closest('[amb-newsletter]') || form }
        }));
      })
      .catch((error) => {
        // Prefer a specific, server-provided reason (e.g. undeliverable email)
        // and fall back to the generic message for network/parse failures.
        this.showError(emailInput, errorEl, (error && error.serverMessage) || options.errorMessage);
      })
      .then(() => {
        this.setLoading(form, submitBtn, false);
      });
  },

  /**
   * Toggle form-local loading: overlay a spinner on the Subscribe label
   * (label stays in the DOM so button width does not jump), block form
   * interaction only, leave the rest of the page untouched.
   *
   * @param {HTMLFormElement} form
   * @param {HTMLElement}     submitBtn
   * @param {boolean}         isLoading
   */
  setLoading(form, submitBtn, isLoading) {
    if (!form || !submitBtn) {
      return;
    }

    const emailInput = form.querySelector('input[type="email"]');
    let spinner = submitBtn.querySelector('.newsletter-form__spinner');

    submitBtn.classList.toggle('newsletter-form__submit--loading', isLoading);
    submitBtn.disabled = isLoading;

    if (emailInput) {
      emailInput.disabled = isLoading;
    }

    if (isLoading) {
      if (!spinner) {
        spinner = document.createElement('span');
        spinner.className = 'newsletter-form__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        submitBtn.appendChild(spinner);
      }

      submitBtn.setAttribute('aria-label', 'Loading');
      return;
    }

    if (spinner) {
      spinner.remove();
    }

    submitBtn.removeAttribute('aria-label');
  },

  /**
   * Resolve a reCAPTCHA token. Gracefully resolves with an empty string when
   * the plugin is disabled/unavailable so the server can decide how to react.
   *
   * The v2 invisible widget writes its response to the widget instance (not
   * reliably to a form field), so we read it back via
   * `grecaptcha.getResponse(widgetId)` after `executeCaptcha` resolves.
   *
   * @param {HTMLFormElement} form
   * @param {string}          action
   *
   * @returns {Promise<string>}
   */
  executeCaptcha(form, action) {
    return new Promise((resolve) => {
      const plugin = window.recaptchaPlugin;

      if (!plugin || !plugin.ENABLED || typeof plugin.executeCaptcha !== 'function') {
        resolve('');
        return;
      }

      const onResolved = () => {
        let token = '';

        try {
          const holder = form.querySelector('.recaptcha-holder');
          const widgetId = holder && window.jQuery ? window.jQuery(holder).data('recaptcha-id') : undefined;

          if (window.grecaptcha && widgetId != null && typeof window.grecaptcha.getResponse === 'function') {
            token = window.grecaptcha.getResponse(widgetId) || '';
          }

          if (!token) {
            const field = form.querySelector('[name="g-recaptcha-response"]');
            token = field ? field.value || '' : '';
          }
        } catch (error) {
          token = '';
        }

        resolve(token);
      };

      plugin.executeCaptcha(form, action || 'newsletter').then(onResolved, () => resolve(''));
    });
  },

  /**
   * The current page URL, used as the CRM lead Origin. Strips nothing so the
   * hash route (e.g. `#!/offers`) is preserved.
   *
   * @returns {string}
   */
  getOrigin() {
    return (window.location && window.location.href) || '';
  },

  /**
   * POST the subscription payload as JSON.
   *
   * @param {string} action
   * @param {string} email
   * @param {string} token
   * @param {string} origin
   * @param {object} options The merged directive options (carries the flags).
   *
   * @returns {Promise<object>}
   */
  postLead(action, email, token, origin, options = {}) {
    return fetch(action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        origin,
        'g-recaptcha-response': token,
        emailValidationEnable: options.emailValidationEnable !== false,
        firstName: options.firstName || 'Website',
        surname: options.surname || 'News Subscriber'
      })
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data || data.error) {
          const error = new Error('Newsletter subscription failed');
          error.serverMessage = data && data.message ? data.message : '';
          throw error;
        }

        return data;
      });
  },

  /**
   * Put the field into the error state: highlight the input (`is-invalid`) and
   * reveal the `invalid-feedback` message (per the v2 style guide).
   *
   * @param {HTMLElement} emailInput
   * @param {HTMLElement} errorEl
   * @param {string}      message
   */
  showError(emailInput, errorEl, message) {
    if (emailInput) {
      emailInput.classList.add('is-invalid');
      emailInput.classList.remove('is-valid');
      emailInput.setAttribute('aria-invalid', 'true');
    }

    this.setText(errorEl, message);
    this.show(errorEl);
  },

  /**
   * Hide the intro + form and reveal the success block. Icon and eyebrow stay
   * visible. Focus moves to the success heading so screen readers announce
   * the outcome.
   *
   * @param {HTMLElement} form
   * @param {HTMLElement} emailInput
   * @param {HTMLElement} errorEl
   * @param {HTMLElement} successEl
   * @param {HTMLElement} introEl
   */
  showSuccess(form, emailInput, errorEl, successEl, introEl) {
    if (emailInput) {
      emailInput.classList.remove('is-invalid');
      emailInput.setAttribute('aria-invalid', 'false');
    }

    this.hide(errorEl);

    if (introEl) {
      introEl.hidden = true;
    }

    if (form) {
      form.hidden = true;
    }

    if (successEl) {
      successEl.hidden = false;

      const heading = successEl.querySelector('.newsletter__success-heading') || successEl;

      if (heading && typeof heading.focus === 'function') {
        heading.focus();
      }
    }
  },

  /**
   * Reset the field to its neutral state (no highlight, no error message).
   *
   * @param {HTMLElement} emailInput
   * @param {HTMLElement} errorEl
   */
  clearState(emailInput, errorEl) {
    if (emailInput) {
      emailInput.classList.remove('is-invalid', 'is-valid');
      emailInput.setAttribute('aria-invalid', 'false');
    }

    this.hide(errorEl);
  },

  /**
   * Writes the message into the feedback element's `[data-message-text]` slot
   * (preserving the leading icon), falling back to the element itself.
   *
   * @param {HTMLElement} element
   * @param {string}      message
   */
  setText(element, message) {
    if (!element) {
      return;
    }

    const textEl = element.querySelector('[data-message-text]') || element;

    textEl.textContent = message || '';
  },

  /**
   * Reveals a Bootstrap feedback element (which is `display: none` by default).
   *
   * @param {HTMLElement} element
   */
  show(element) {
    if (element) {
      element.classList.add('d-flex');
    }
  },

  /**
   * @param {HTMLElement} element
   */
  hide(element) {
    if (element) {
      element.classList.remove('d-flex');
    }
  }
};
